import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FileSignature, Download, Eye, Sparkles, Loader2, AlertCircle, CheckCircle2, Package } from "lucide-react";
import { toast } from "sonner";

/** Pick the most-recently-created doc matching `templateKey`. */
function pickLatest(documents, templateKey) {
  return (documents || [])
    .filter((d) => d.template_key === templateKey)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
}

/**
 * ANC-discipline admin actions panel — renders only for matter_type='antenuptial'.
 * Rows are gated by `entities.marriage.regime`:
 *   out_with_accrual  → show ANC With Accrual + POA
 *   out_no_accrual    → show ANC Without Accrual + POA
 *   (anything else)   → show both ANC variants + POA (admin picks)
 */
const ALL_TEMPLATES = [
  { key: "anc_with_accrual",      label: "Antenuptial Contract — WITH Accrual",     blurb: "Full notarial ANC including the application of the accrual system (Chapter 1, MPA 88 of 1984)." },
  { key: "anc_without_accrual",   label: "Antenuptial Contract — WITHOUT Accrual",  blurb: "Notarial ANC excluding the accrual system — complete separation of estates." },
  { key: "anc_power_of_attorney", label: "Power of Attorney — ANC Notarial",        blurb: "Both spouses authorise the firm's appearer to sign the ANC before the Notary." },
];

function templatesForRegime(regime) {
  if (regime === "out_with_accrual") {
    return [ALL_TEMPLATES[0], ALL_TEMPLATES[2]];
  }
  if (regime === "out_no_accrual") {
    return [ALL_TEMPLATES[1], ALL_TEMPLATES[2]];
  }
  return ALL_TEMPLATES;
}

export default function AncDocumentActions({ matter, documents, onChanged }) {
  if ((matter?.matter_type || matter?.service_type) !== "antenuptial") return null;

  const regime    = matter?.entities?.marriage?.regime;
  const templates = templatesForRegime(regime);
  const anyDraft  = templates.some((t) => pickLatest(documents, t.key));

  const downloadBundle = () => {
    api.get(`/admin/matters/${matter.id}/anc-bundle.zip`, { responseType: "blob" })
      .then((res) => {
        const blob = new Blob([res.data], { type: "application/zip" });
        const ref  = (matter.id || "").slice(0, 8).toUpperCase();
        const spouses = (matter.entities?.persons || [])
          .filter((p) => p.role === "spouse")
          .map((p) => (p.full_name || "Spouse").split(" ").slice(-1)[0])
          .join("_and_") || "Spouses";
        const name = spouses.replace(/[^A-Za-z0-9_]/g, "_").slice(0, 48);
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `EH_Inc_ANC_Submission_${name}_${ref}.zip`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(a.href);
        toast.success("ANC submission bundle downloaded");
      })
      .catch((e) => toast.error(formatApiError(e)));
  };

  return (
    <div className="bg-white border border-zinc-200 mb-5" data-testid="anc-doc-actions">
      <div className="px-5 py-3 bg-zinc-900 text-white flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <FileSignature className="h-4 w-4" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-300">EH Inc · ANC Workspace</div>
            <div className="text-sm font-medium">
              {regime === "out_with_accrual"
                ? "Antenuptial Contract — With Accrual"
                : regime === "out_no_accrual"
                ? "Antenuptial Contract — Without Accrual"
                : "Antenuptial Contract — Awaiting Regime"}
            </div>
          </div>
        </div>
        {anyDraft && (
          <button
            type="button"
            onClick={downloadBundle}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider font-medium bg-white text-zinc-900 hover:bg-zinc-100"
            data-testid="download-anc-bundle-btn"
          >
            <Package className="h-3.5 w-3.5" />
            Download ANC Submission Bundle (ZIP)
          </button>
        )}
      </div>

      {!regime && (
        <div className="px-5 py-3 bg-amber-50 border-b border-amber-200 text-xs text-amber-900" data-testid="anc-regime-missing">
          Set <code className="bg-amber-100 px-1">entities.marriage.regime</code> to{" "}
          <code className="bg-amber-100 px-1">out_with_accrual</code> or{" "}
          <code className="bg-amber-100 px-1">out_no_accrual</code> to lock the variant. Both options shown below.
        </div>
      )}

      <ul className="divide-y divide-zinc-200">
        {templates.map((t) => (
          <AncRow
            key={t.key}
            label={t.label}
            blurb={t.blurb}
            templateKey={t.key}
            matter={matter}
            doc={pickLatest(documents, t.key)}
            onChanged={onChanged}
          />
        ))}
      </ul>
    </div>
  );
}


function AncRow({ label, blurb, templateKey, matter, doc, onChanged }) {
  const [busy, setBusy]       = useState(false);
  const [missing, setMissing] = useState(null);

  const generate = async () => {
    setBusy(true);
    setMissing(null);
    try {
      const { data } = await api.post(
        `/admin/matters/${matter.id}/documents/generate-structured`,
        { template_key: templateKey },
      );
      toast.success(`${label} generated`, { description: data?.audit_event?.summary || "Draft ready for review." });
      onChanged?.();
    } catch (e) {
      const status = e?.response?.status;
      const body   = e?.response?.data;
      if (status === 422 && body?.blocked) {
        setMissing(body.missing || []);
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setBusy(false);
    }
  };

  const previewPdf = () => {
    if (!doc) return;
    api.get(`/admin/documents/${doc.id}/pdf`, { responseType: "blob" })
      .then((res) => {
        const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        window.open(url, "_blank", "noopener,noreferrer");
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      })
      .catch((e) => toast.error(formatApiError(e)));
  };

  const downloadPdf = () => {
    if (!doc) return;
    api.get(`/admin/documents/${doc.id}/pdf`, { responseType: "blob" })
      .then((res) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        a.download = `${(doc.template_name || label).replace(/\s+/g, "_")}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(a.href);
      })
      .catch((e) => toast.error(formatApiError(e)));
  };

  return (
    <li className="p-5" data-testid={`anc-row-${templateKey}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="font-medium text-sm text-zinc-900">{label}</div>
          <div className="text-xs text-zinc-500 mt-1">{blurb}</div>
          {doc && (
            <div className="text-[11px] text-zinc-500 mt-2 flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1 text-emerald-700">
                <CheckCircle2 className="h-3 w-3" /> Draft generated
              </span>
              <span className="text-zinc-400">·</span>
              <span>{new Date(doc.created_at).toLocaleString()}</span>
              <span className="text-zinc-400">·</span>
              <span className="uppercase tracking-wider text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5">
                {(doc.status || "").replace(/_/g, " ")}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {doc ? (
            <>
              <Button variant="outline" size="sm" className="rounded-none" onClick={previewPdf}
                      data-testid={`preview-${templateKey}-btn`}>
                <Eye className="h-3.5 w-3.5 mr-1.5" /> Preview
              </Button>
              <Button size="sm" className="rounded-none bg-zinc-900 hover:bg-zinc-800" onClick={downloadPdf}
                      data-testid={`download-${templateKey}-btn`}>
                <Download className="h-3.5 w-3.5 mr-1.5" /> Download
              </Button>
              <Link to={`/admin/matters/${matter.id}/documents/${doc.id}`}
                    className="text-xs text-zinc-500 hover:text-zinc-900 px-2"
                    data-testid={`open-${templateKey}-workspace`}>
                Workspace →
              </Link>
            </>
          ) : (
            <Button size="sm" className="rounded-none bg-zinc-900 hover:bg-zinc-800" onClick={generate} disabled={busy}
                    data-testid={`generate-${templateKey}-btn`}>
              {busy
                ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Generating…</>
                : <><Sparkles className="h-3.5 w-3.5 mr-1.5" /> Generate</>}
            </Button>
          )}
        </div>
      </div>

      {missing && missing.length > 0 && (
        <div className="mt-3 border border-amber-300 bg-amber-50 p-3" data-testid={`anc-missing-${templateKey}`}>
          <div className="text-xs font-medium text-amber-900 flex items-center gap-1.5">
            <AlertCircle className="h-3.5 w-3.5" />
            Complete required fields to generate {label}
          </div>
          <ul className="text-[12px] text-amber-800 list-disc pl-5 mt-1.5 space-y-0.5">
            {missing.map((f) => <li key={f}>{f}</li>)}
          </ul>
        </div>
      )}
    </li>
  );
}
